// 🔥 www 리다이렉트 (가장 먼저 실행)
if (window.location.hostname.startsWith('www.')) {
    const newHost = window.location.hostname.substring(4); // "www." 제거
    const newUrl = `${window.location.protocol}//${newHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
    console.log('🔄 www 리다이렉트:', window.location.href, '→', newUrl);
    window.location.replace(newUrl); // 301 리다이렉트 효과
}

import { createApp } from 'vue/dist/vue.esm-bundler.js';
import App from './App.vue';
import router from './router';
import store from './store';
import axios from 'axios';
import { tokenUtils } from '@/utils/auth.js';

// 🔥 관리자 페이지 여부 확인
const isAdminPage = window.location.pathname.startsWith('/admin');
// 🔥 입점사(셀러) 페이지 여부 확인 — 셀러도 관리자처럼 global.css 미사용
const isSellerPage = window.location.pathname.startsWith('/seller');

// 🔥 조건부 CSS 로딩
if (!isAdminPage && !isSellerPage) {
    // 사용자(쇼핑몰) 페이지에서만 global.css 로드
    import('./assets/styles/global.css');
}

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Chart.js import 추가
import 'chart.js/auto';

// AG Grid
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import { ModuleRegistry } from 'ag-grid-community';
import { ClientSideRowModelModule, PaginationModule } from 'ag-grid-community';
ModuleRegistry.registerModules([ClientSideRowModelModule, PaginationModule]);

// 스타일
import './assets/styles/shop.css';
import './assets/styles/admin.css';
import './assets/styles/seller.css';

// 🔥 라우터 변경 시 CSS 동적 로딩/제거
router.beforeEach((to, from, next) => {
    // 🔥 입점사 페이지도 관리자처럼 global.css 미사용 처리
    const toAdmin = to.path.startsWith('/admin') || to.path.startsWith('/seller');
    const fromAdmin = from.path.startsWith('/admin') || from.path.startsWith('/seller');

    // 🔥 관리자 ↔ 사용자 페이지 전환 시
    if (toAdmin !== fromAdmin) {
        if (toAdmin) {
            // 🔥 관리자 페이지로 이동: global.css 제거
            removeGlobalCSS();
        } else {
            // 🔥 사용자 페이지로 이동: global.css 추가
            addGlobalCSS();
        }
    }

    next();
});
// 🔥 global.css 동적 추가 함수
function addGlobalCSS() {
    // 이미 로드되어 있는지 확인
    if (!document.querySelector('link[data-global-css]') &&
        !document.querySelector('style[data-global-css]')) {
        import('./assets/styles/global.css').then(() => {
            console.log('✅ global.css 동적 로드 완료');
        });
    }
}

// 🔥 global.css 동적 제거 함수
function removeGlobalCSS() {
    // link 태그로 로드된 경우
    const linkElements = document.querySelectorAll('link[href*="global.css"]');
    linkElements.forEach(link => link.remove());

    // style 태그로 로드된 경우 (Vite/Webpack에서 인라인으로 주입된 경우)
    const styleElements = document.querySelectorAll('style');
    styleElements.forEach(style => {
        if (style.textContent && style.textContent.includes('--primary-color')) {
            style.remove();
        }
    });

    console.log('✅ global.css 동적 제거 완료');
}

// 🔥 이미지 헬퍼 import
import { getImageUrl, getDefaultImage, handleImageError, setupImageErrorHandling } from '@/utils/imageHelper.js';

const app = createApp(App);

// 🔥 글로벌 이미지 처리 함수들
app.config.globalProperties.$getImageUrl = getImageUrl;
app.config.globalProperties.$getDefaultImage = getDefaultImage;
app.config.globalProperties.$handleImageError = handleImageError;
app.config.globalProperties.$getUploadUrl = getUploadUrl;

// 🔥 자동 이미지 오류 처리 설정
app.config.globalProperties.$setupImageErrorHandling = setupImageErrorHandling;

// API URL 폴백 설정 함수
function getApiBaseUrl() {
    const hostname = window.location.hostname;
    const port = window.location.port;
    const isAdminPage = window.location.pathname.startsWith('/admin');

    console.log('🌐 현재 호스트:', hostname, '포트:', port, '관리자페이지:', isAdminPage);

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        console.log('🔧 로컬 개발 환경 감지');
        return 'http://localhost:8080';
    } else if (hostname === '13.125.214.191') {
        console.log('🚀 EC2 IP 직접 접속 감지');
        return 'http://13.125.214.191:8080';
    } else if (hostname.includes('doctormarket.co.kr')) {
        // 🔥 co.kr 접속자는 co.kr API를 그대로 호출 (같은 도메인 유지)
        console.log('🌍 도메인 환경 감지 - doctormarket.co.kr');

        if (isAdminPage) {
            console.log('🔐 관리자 페이지 - EC2 HTTP API 직접 사용');
            return 'http://13.125.214.191:8080';
        }

        if (process.env.NODE_ENV === 'production') {
            return 'https://doctormarket.co.kr';
        } else {
            return process.env.VUE_APP_FALLBACK_API_URL || 'http://13.125.214.191:8080';
        }
    } else if (hostname.includes('doctormarket.kr')) {
        console.log('🌍 도메인 환경 감지 - doctormarket.kr');

        if (isAdminPage) {
            console.log('🔐 관리자 페이지 - EC2 HTTP API 직접 사용');
            return 'http://13.125.214.191:8080';
        }

        if (process.env.NODE_ENV === 'production') {
            return 'https://doctormarket.kr';
        } else {
            return process.env.VUE_APP_FALLBACK_API_URL || 'http://13.125.214.191:8080';
        }
    } else {
        console.log('❓ 기타 환경 감지, 폴백 API 사용');
        return process.env.VUE_APP_FALLBACK_API_URL || 'http://13.125.214.191:8080';
    }
}

// 🔥 글로벌 업로드 URL 생성 함수 추가 (기존 함수 유지)
function getUploadUrl(endpoint = '/api/admin/upload') {
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}${endpoint}`;
}

// axios 기본 설정
const apiBaseUrl = getApiBaseUrl();
axios.defaults.baseURL = apiBaseUrl;
axios.defaults.headers.common['Content-Type'] = 'application/json';

console.log('✅ API Base URL 설정:', apiBaseUrl);

// 🔥 요청 인터셉터
axios.interceptors.request.use(
    (config) => {
        const isAdminPage = window.location.pathname.startsWith('/admin');
        const isSellerPage = window.location.pathname.startsWith('/seller');

        let token = null;
        if (isAdminPage) {
            // 관리자: localStorage 또는 sessionStorage의 adminToken 사용
            token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        } else if (isSellerPage) {
            // 🔥 2026-07-28 R3: 입점사도 sessionStorage만 사용 (브라우저 닫으면 로그아웃)
            token = sessionStorage.getItem('sellerToken');
        } else {
            // 🔥 사용자: sessionStorage의 auth_token만 사용 (브라우저 닫으면 로그아웃)
            token = sessionStorage.getItem('auth_token');
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        console.log('🔥 API 요청:', config.method.toUpperCase(), config.url);
        console.log('🔥 페이지 타입:', isAdminPage ? '관리자' : '일반');
        console.log('🔥 토큰 상태:', token ? '있음' : '없음');

        return config;
    },
    (error) => {
        console.error('🔥 요청 인터셉터 오류:', error);
        return Promise.reject(error);
    }
);

// 🔥 응답 인터셉터 (401 에러 처리)
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.error('🔥 API 응답 오류:', error.response?.status, error.response?.data);

        if (error.response && error.response.status === 401) {
            console.log('🔥 인증 오류 발생 - 토큰 정리 중...');

            const isAdminPage = window.location.pathname.startsWith('/admin');
            const isSellerPage = window.location.pathname.startsWith('/seller');
            // 🔥 2026-08-26 파트너 포털 분기 추가.
            //    이 분기가 없어서 파트너 화면의 401 이 맨 아래 else(사용자몰)로 빠졌고,
            //    로그인 실패 시 쇼핑몰 로그인(/login)으로 튕기고 있었다.
            //    ⚠️ 기존 admin/seller/사용자몰 분기는 그대로 둔다.
            const isPartnerPage = window.location.pathname.startsWith('/partner');

            if (isPartnerPage) {
                sessionStorage.removeItem('partner_token');
                sessionStorage.removeItem('partner_info');

                if (router.currentRoute.value.path !== '/partner/login') {
                    alert('인증에 실패했습니다. 다시 로그인해주세요.');
                    router.push('/partner/login');
                }
            } else if (isAdminPage) {
                localStorage.removeItem('adminToken');
                sessionStorage.removeItem('adminToken');
                localStorage.removeItem('auth_token');
                sessionStorage.removeItem('auth_token');

                if (router.currentRoute.value.path !== '/admin/login') {
                    alert('인증에 실패했습니다. 다시 로그인해주세요.');
                    router.push('/admin/login');
                }
            } else if (isSellerPage) {
                // 🔥 2026-07-28 R3: sessionStorage 기준. 구버전 localStorage 잔재도 함께 정리
                sessionStorage.removeItem('sellerToken');
                sessionStorage.removeItem('seller_info');
                localStorage.removeItem('sellerToken');
                localStorage.removeItem('seller_info');

                if (router.currentRoute.value.path !== '/seller/login') {
                    alert('인증에 실패했습니다. 다시 로그인해주세요.');
                    router.push('/seller/login');
                }
            } else {
                localStorage.removeItem('auth_token');
                sessionStorage.removeItem('auth_token');

                if (router.currentRoute.value.path !== '/login') {
                    alert('인증에 실패했습니다. 다시 로그인해주세요.');
                    router.push('/login');
                }
            }
        }

        return Promise.reject(error);
    }
);

// 🔥 토큰 만료 자동 감지 및 로그아웃 처리
function setupTokenExpirationCheck() {
    setInterval(() => {
        const isAdminPage = window.location.pathname.startsWith('/admin');
        const isSellerPage = window.location.pathname.startsWith('/seller');

        let token = null;
        if (isAdminPage) {
            token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        } else if (isSellerPage) {
            // 🔥 2026-07-28 R3: 입점사도 sessionStorage만 사용
            token = sessionStorage.getItem('sellerToken');
        } else {
            token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        }

        // 토큰이 있지만 만료된 경우 (데모 셀러 목업 토큰은 JWT가 아니므로 제외)
        if (token && token !== 'demo-seller-token' && !tokenUtils.isTokenValid(token)) {
            console.log('🔥 토큰 만료 감지 - 자동 로그아웃 처리');

            if (isAdminPage) {
                localStorage.removeItem('adminToken');
                sessionStorage.removeItem('adminToken');
                localStorage.removeItem('auth_token');
                sessionStorage.removeItem('auth_token');
                localStorage.removeItem('adminUser');
                localStorage.removeItem('admin_user');

                if (router.currentRoute.value.path !== '/admin/login') {
                    alert('로그인 시간이 만료되었습니다. 다시 로그인해주세요.');
                    router.push('/admin/login');
                }
            } else if (isSellerPage) {
                // 🔥 2026-07-28 R3: 입점사는 sessionStorage 기준. 구버전 localStorage 잔재도 함께 정리
                sessionStorage.removeItem('sellerToken');
                sessionStorage.removeItem('seller_info');
                localStorage.removeItem('sellerToken');
                localStorage.removeItem('seller_info');

                if (router.currentRoute.value.path !== '/seller/login') {
                    alert('로그인 시간이 만료되었습니다. 다시 로그인해주세요.');
                    router.push('/seller/login');
                }
            } else {
                localStorage.removeItem('auth_token');
                sessionStorage.removeItem('auth_token');
                localStorage.removeItem('user_info');

                if (router.currentRoute.value.path !== '/login') {
                    alert('로그인 시간이 만료되었습니다. 다시 로그인해주세요.');
                    router.push('/login');
                }
            }
        }
    }, 60000); // 1분마다 확인
}

// �� 모든 이미지 URL을 자동으로 변환하는 함수 추가 (위로 이동)
function transformAllImageUrls() {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
        if (img.src && !img.src.startsWith('data:') && !img.src.startsWith('blob:')) {
            // 🔥 입점사 업로드 임시파일(/temp/)은 로컬에만 있을 수 있어 변환 제외 (상품 이미지 등 기존은 그대로 변환)
            if (img.src.includes('/uploads/thumb/temp/')) return;
            // localhost:8080을 운영서버 URL로 변환
            if (img.src.includes('localhost:8080')) {
                const newSrc = img.src.replace('http://localhost:8080', 'http://13.125.214.191:8080');
                img.src = newSrc;
                console.log('🔄 이미지 URL 변환:', img.src, '→', newSrc);
            }
        }
    });
}

// 🔥 페이지 이동 시 이미지 오류 처리 재설정
router.afterEach((to) => {
    // 🔥 상품 목록 페이지는 스크롤 처리 완전히 제외 (ProductListView에서 자체 처리)
    const isProductListPage = to.path === '/products' || to.name === 'shop-products';

    if (!isProductListPage) {
        setTimeout(() => {
            // 스크롤 초기화
            window.scrollTo({ top: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;

            if (window.pageYOffset !== undefined) {
                window.pageYOffset = 0;
            }
        }, 150);
    }

    // 🔥 이미지 오류 처리 재설정
    setTimeout(() => {
        setupImageErrorHandling();
        transformAllImageUrls();

        // 🔥 페이지 제목 업데이트
        updatePageTitle();
    }, 150);
});

// 🔥 사이트 제목 캐싱
let cachedSiteTitle = null;

// 🔥 사이트 제목 로드 및 업데이트 함수 (캐싱 추가)
async function loadSiteTitle() {
    // 캐시가 있으면 API 호출 안함
    if (cachedSiteTitle) {
        document.title = cachedSiteTitle;
        return;
    }

    try {
        const response = await axios.get('/api/config/store');
        if (response.data && response.data.name) {
            cachedSiteTitle = response.data.name;
            document.title = cachedSiteTitle;
            console.log('✅ 사이트 제목 업데이트:', cachedSiteTitle);
        } else {
            console.log('⚠️ 사이트 제목이 설정되지 않음, 기본값 사용');
            cachedSiteTitle = 'doctormarket - 닥터마켓 쇼핑몰';
            document.title = cachedSiteTitle;
        }
    } catch (error) {
        console.error('❌ 사이트 제목 로드 실패:', error);
        cachedSiteTitle = 'doctormarket - 닥터마켓 쇼핑몰';
        document.title = cachedSiteTitle;
    }
}

// 🔥 페이지별 제목 업데이트 함수
function updatePageTitle() {
    const currentPath = router.currentRoute.value.path;

    // 🔥 2026-07-28 R2: 입점사센터는 의뢰자가 지정한 고정 문구 사용 (사이트명 API 호출 불필요)
    if (currentPath.startsWith('/seller')) {
        document.title = '입점사센터 - 닥터마켓';
        return;
    }

    // 🔥 2026-08-26 영업사원 파트너 포털도 고정 문구 사용 (입점사센터와 같은 방식)
    //    ⚠️ 전역 index.html 의 <title> 은 건드리지 않는다. 그걸 바꾸면 쇼핑몰 탭 이름도 같이 바뀐다.
    if (currentPath.startsWith('/partner')) {
        document.title = '닥터마켓 파트너 포탈';
        return;
    }

    // 관리자 페이지인 경우
    if (currentPath.startsWith('/admin')) {
        loadSiteTitle().then(() => {
            const currentTitle = document.title;
            document.title = `관리자 - ${currentTitle}`;
        });
    } else {
        // 일반 사용자 페이지
        loadSiteTitle();
    }
}

app.config.globalProperties.$axios = axios;

app.use(router);
app.use(store);

// 🔥 앱 마운트 후 이미지 오류 처리 설정
app.mount('#app');

// 🔥 토큰 만료 자동 감지 시작
setupTokenExpirationCheck();

// 🔥 DOM 로드 완료 후 자동 이미지 오류 처리 시작
document.addEventListener('DOMContentLoaded', () => {
    setupImageErrorHandling();
});

// 🔥 즉시 실행 (이미 DOM이 로드된 경우)
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setupImageErrorHandling();
        transformAllImageUrls(); // 여기도 추가
    });
} else {
    setupImageErrorHandling();
    transformAllImageUrls(); // 여기도 추가
}